import React, { MouseEventHandler, PropsWithChildren, useEffect, useState } from 'react'

import { useModalInstance, useModalTabs } from '@context/ModalInstanceContext'

import { ModalBodyCardSelect } from '@ui/components/Modal/Body/CardSelect'
import { ModalBodyInput } from '@ui/components/Modal/Body/Input'
import { ModalBodyListGroup } from '@ui/components/Modal/Body/ListGroup'
import { ModalBodyStatus } from '@ui/components/Modal/Body/Status'
import { ModalBodyTable } from '@ui/components/Modal/Body/Table'
import { ModalBodyFieldGroups } from '@ui/components/Modal/Body/TableFields'
import { ModalBodyTabs } from '@ui/components/Modal/Body/Tabs'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useScrollEdges } from '@hooks/useScrollEdges'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { ModalBodyColumns } from './Columns'
import { ModalBodyImage } from './Image'
import { ModalBodyLabeledInfo } from './LabeledInfo'
import { ModalBodyList } from './List'
import { ModalBodyText } from './Text'
import { ModalBodyTitle } from './Title'

import styles from './Body.module.scss'

interface ModalBodyRootProps extends PropsWithChildren {
  centered?: boolean
  isLoading?: boolean
  onClick?: MouseEventHandler<HTMLDivElement>
  isScrollHintNeeded?: boolean
  hideScrollShadows?: boolean
}

const ModalBodyRoot: React.FC<ModalBodyRootProps> = ({
  children,
  centered,
  onClick,
  isLoading = false,
  isScrollHintNeeded,
  hideScrollShadows = false,
}) => {
  const { contentRef, contentInnerRef, setIsLoading, containerMode } = useModalInstance()
  const tabsCtx = useModalTabs()
  const { canScrollUp, canScrollDown } = useScrollEdges(contentRef, contentInnerRef, [
    children,
    tabsCtx?.activeId,
  ])

  const [showScrollHint, setShowScrollHint] = useState(false)

  const isFullScreenContainer = containerMode === 'fullScreen'

  useEffect(() => {
    if (canScrollUp) {
      setShowScrollHint(false)
    }
  }, [canScrollUp])

  useEffect(() => {
    if (!isFullScreenContainer) {
      setShowScrollHint(false)
      return
    }

    if (!canScrollDown) {
      setShowScrollHint(false)
      return
    }

    setShowScrollHint(true)
    const timeout = setTimeout(() => {
      setShowScrollHint(false)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [canScrollDown, isFullScreenContainer])

  useEffect(() => {
    setIsLoading(isLoading)
  }, [setIsLoading, isLoading])

  const scrollClasses = [
    !hideScrollShadows && canScrollUp && styles.showTopShadow,
    !hideScrollShadows && canScrollDown && styles.showBottomShadow,
  ]
    .filter(Boolean)
    .join(' ')

  const isScrollHintVisible =
    containerMode === 'fullScreen' && isScrollHintNeeded && showScrollHint && canScrollDown

  return (
    <div onClick={onClick} className={`${styles.modalBodyWrapper} ${scrollClasses}`}>
      <div ref={contentRef} className={styles.modalBody}>
        <Box
          flex
          direction='column'
          alignSelf='stretch'
          textAlign={centered ? 'center' : 'start'}
          justify={centered ? 'center' : 'flex-start'}
          align={centered ? 'center' : 'flex-start'}
          gap='x16'
          ref={contentInnerRef}
        >
          <Loading fullWidth isLoading={isLoading} showLoadingIcon>
            {children}
          </Loading>
        </Box>
      </div>
      {isScrollHintVisible && (
        <div className={styles.scrollHint}>
          <ArrowIcon.Down color='text-white' />
        </div>
      )}
    </div>
  )
}

const ModalBodyTitleTextBox: React.FC<PropsWithChildren> = ({ children }) => (
  <Box flex direction='column' gap='x8'>
    {children}
  </Box>
)

type ModalBodyType = React.FC<ModalBodyRootProps> & {
  Title: typeof ModalBodyTitle
  Text: typeof ModalBodyText
  TitleTextBox: typeof ModalBodyTitleTextBox
  Input: typeof ModalBodyInput
  List: typeof ModalBodyList
  Columns: typeof ModalBodyColumns
  Image: typeof ModalBodyImage
  Table: typeof ModalBodyTable
  LabeledInfo: typeof ModalBodyLabeledInfo
  CardSelect: typeof ModalBodyCardSelect
  FieldGroups: typeof ModalBodyFieldGroups
  ListGroup: typeof ModalBodyListGroup
  Status: typeof ModalBodyStatus
  Tabs: typeof ModalBodyTabs
}

export const ModalBody = Object.assign(ModalBodyRoot, {
  Title: ModalBodyTitle,
  Text: ModalBodyText,
  TitleTextBox: ModalBodyTitleTextBox,
  Input: ModalBodyInput,
  List: ModalBodyList,
  Columns: ModalBodyColumns,
  Image: ModalBodyImage,
  Table: ModalBodyTable,
  LabeledInfo: ModalBodyLabeledInfo,
  CardSelect: ModalBodyCardSelect,
  FieldGroups: ModalBodyFieldGroups,
  ListGroup: ModalBodyListGroup,
  Status: ModalBodyStatus,
  Tabs: ModalBodyTabs,
}) as ModalBodyType
